import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { useAuth } from '../../AuthContext'
import ModalOverlay from './ModalOverlay'

// 共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
const INPUT = 'h-8 rounded-lg border border-border-input bg-surface px-2.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const TEXTAREA = 'w-full resize-y rounded-lg border border-border-input bg-surface px-2.5 py-1.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const TH = 'text-secondary border-b border-secondary px-2.5 py-1.5 text-left text-[11px] font-semibold whitespace-nowrap'
const TD = 'text-primary px-2.5 py-1.5 align-middle'
const LABEL = 'text-secondary text-[11px] font-medium'
const FIELD = 'flex flex-col gap-1'

// NGワード・禁止トピック管理（仕様書 3.8 / メニュー⑨）
export default function AIPurchaseAssistNgRules() {
  const { siteId } = useParams()
  const { authHeaders } = useAuth()

  const [tab, setTab] = useState('ng_word')
  const [rules, setRules] = useState([])
  const [counts, setCounts] = useState({ ng_word: 0, banned_topic: 0, force_escalation: 0 })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showTest, setShowTest] = useState(false)

  const fetchAll = async () => {
    if (tab === 'detection_logs') return  // 別タブは別フェッチ
    setLoading(true)
    setError(null)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/ng-rules?rule_type=${tab}`, { headers: authHeaders() })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      setRules(d.items || [])
      setCounts(d.counts_by_type || counts)
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchAll() }, [siteId, tab])

  const seedDefaults = async () => {
    if (!window.confirm('プリセット辞書（NGワード/禁止トピック/エスカレーション）を投入します。\n既存ルールは保持され、重複はスキップされます。')) return
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/ng-rules/seed-defaults`, {
      method: 'POST', headers: authHeaders(),
    })
    if (r.ok) {
      const d = await r.json()
      alert(`プリセット投入完了: 追加 ${d.created} 件 / 既存スキップ ${d.skipped} 件`)
      fetchAll()
    } else {
      const d = await r.json().catch(() => ({}))
      alert(`投入失敗: ${d.detail || r.status}`)
    }
  }

  const exportRules = async () => {
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/ng-rules/export?rule_type=${tab}`, { headers: authHeaders() })
    if (!r.ok) {
      alert('エクスポート失敗')
      return
    }
    const d = await r.json()
    const blob = new Blob([JSON.stringify(d, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `ng-rules-${tab}-site${siteId}-${new Date().toISOString().slice(0, 10)}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const importRules = async (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    try {
      const text = await file.text()
      const json = JSON.parse(text)
      const items = json.items || json
      if (!Array.isArray(items)) {
        alert('JSON形式が正しくありません（items配列が必要）')
        return
      }
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/ng-rules/bulk-import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ rule_type: tab, items }),
      })
      const d = await r.json()
      if (r.ok) {
        alert(`インポート完了: 追加 ${d.created} 件 / スキップ ${d.skipped} 件`)
        fetchAll()
      } else {
        alert(`インポート失敗: ${d.detail || r.status}`)
      }
    } catch (e) {
      alert(`ファイル読込失敗: ${e.message}`)
    }
    event.target.value = ''
  }

  const TABS = [
    { key: 'ng_word', label: '🚫 NGワード', active: 'bg-surface-critical text-critical' },
    { key: 'banned_topic', label: '🚧 禁止トピック', active: 'bg-surface-warning text-warning' },
    { key: 'force_escalation', label: '📞 強制エスカレーション', active: 'bg-surface-emphasis text-emphasis' },
    { key: 'detection_logs', label: '📋 検知ログ', active: 'bg-surface-success text-success' },
  ]

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='🚫 NGワード・禁止トピック'
        description='ブランド毀損・法令違反リスクを回避するためのガードレール設定。サイト全体に適用されます。'
        rightNode={
          <div className='flex flex-wrap items-center gap-1.5'>
            <Button color={ButtonColor.PRIMARY} onClick={() => setShowTest(true)}>🧪 検知テスト</Button>
            <Button color={ButtonColor.SECONDARY} onClick={seedDefaults}>📦 プリセット投入</Button>
            <Button color={ButtonColor.SECONDARY} onClick={exportRules}>📤 エクスポート</Button>
            <label className='text-primary bg-fill-primary shadow-button hover:bg-fill-hover inline-flex h-6.5 cursor-pointer items-center rounded-lg px-3 text-size-12'>
              📥 インポート
              <input type='file' accept='.json' onChange={importRules} className='hidden' />
            </label>
          </div>
        }
      />

      {error && (
        <div className='bg-surface-critical text-critical mb-3 rounded-lg p-2.5 text-xs'>
          取得エラー: {error}
        </div>
      )}

      {/* タブ */}
      <div className='mb-3.5 flex flex-wrap gap-1.5 border-b border-secondary pb-2'>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`cursor-pointer rounded-md px-3 py-1.5 text-[12px] font-semibold ${tab === t.key ? t.active : 'text-secondary hover:bg-surface-hover'}`}
          >{t.label} <span className='text-[10px] opacity-70'>({counts[t.key] || 0})</span></button>
        ))}
      </div>

      {/* 現在のタブ向け説明 */}
      {tab !== 'detection_logs' && (
        <div className={`${CARD} text-secondary mb-3 p-2.5 text-[11px]`} style={{ borderLeft: '4px solid var(--color-fill-emphasis)' }}>
          {tab === 'ng_word' && '応答に含まれてはいけないワードリスト。AI出力に含まれた場合、設定したアクション（block/log_only/escalate）が実行されます。'}
          {tab === 'banned_topic' && 'AIが扱ってはいけないトピック。ユーザーがそのトピックについて質問した場合、回答せずに設定したアクションを実行します。'}
          {tab === 'force_escalation' && '人間オペレータ or AIスタッフへの強制引き継ぎトリガー。クレーム・解約・返品等の重要な問い合わせに使用。'}
        </div>
      )}

      {/* 検知ログタブ */}
      {tab === 'detection_logs' ? (
        <DetectionLogsView siteId={siteId} authHeaders={authHeaders} />
      ) : (
        <>
          {/* 一覧 */}
          <RuleList
            ruleType={tab}
            rules={rules}
            loading={loading}
            siteId={siteId}
            authHeaders={authHeaders}
            onChanged={fetchAll}
          />

          {/* 新規追加フォーム */}
          <NewRuleForm ruleType={tab} siteId={siteId} authHeaders={authHeaders} onCreated={fetchAll} />
        </>
      )}

      {/* 検知テストオーバーレイ */}
      {showTest && (
        <DetectionTestOverlay siteId={siteId} authHeaders={authHeaders} onClose={() => setShowTest(false)} />
      )}
    </Main>
  )
}


// =====================
// ルールリスト
// =====================
function RuleList({ ruleType, rules, loading, siteId, authHeaders, onChanged }) {
  if (loading) return <div className='text-secondary py-8 text-center text-xs'>読み込み中...</div>
  if (rules.length === 0) return (
    <div className={`${CARD} text-tertiary py-8 text-center text-xs`}>
      ルールが登録されていません。<br />「プリセット投入」または下のフォームから追加してください。
    </div>
  )

  // カテゴリ別にグルーピング
  const grouped = {}
  for (const r of rules) {
    const cat = r.category || '(未分類)'
    if (!grouped[cat]) grouped[cat] = []
    grouped[cat].push(r)
  }

  // カテゴリ表示順序（不適切表現は最下部、未分類はその前）
  const CATEGORY_ORDER = [
    // 上部: 重要度高
    'price_guarantee', 'pii_request', 'medical', 'legal', 'financial', 'political',
    'complaint', 'cancellation', 'return', 'refund',
    // 中部: その他カテゴリは アルファベット順
    // 下部: 攻撃的表現等は最後
    'competitor',
    '(未分類)',
    'inappropriate',
  ]
  const sortedEntries = Object.entries(grouped).sort(([a], [b]) => {
    const ai = CATEGORY_ORDER.indexOf(a)
    const bi = CATEGORY_ORDER.indexOf(b)
    if (ai === -1 && bi === -1) return a.localeCompare(b)
    if (ai === -1) return -1  // 未定義は中段（攻撃系より上）
    if (bi === -1) return 1
    return ai - bi
  })

  return (
    <div className='mb-4 flex flex-col gap-3.5'>
      {sortedEntries.map(([cat, list]) => (
        <div key={cat} className={`${CARD} p-3`}>
          <h3 className='text-primary m-0 mb-2 text-xs font-bold'>
            📁 {cat} <span className='text-tertiary text-[10px] font-normal'>({list.length})</span>
          </h3>
          <div className='flex flex-col gap-1.5'>
            {list.map(r => (
              <RuleRow key={r.id} rule={r} ruleType={ruleType} siteId={siteId} authHeaders={authHeaders} onChanged={onChanged} />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}


// =====================
// 1ルール行
// =====================
function RuleRow({ rule, ruleType, siteId, authHeaders, onChanged }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState({ ...rule })
  const [saving, setSaving] = useState(false)

  const ACTION_BADGE = {
    block: { label: '🚫 ブロック', cls: 'bg-surface-critical text-critical' },
    log_only: { label: '📝 ログのみ', cls: 'bg-surface-warning text-warning' },
    escalate: { label: '📞 エスカレ', cls: 'bg-surface-emphasis text-emphasis' },
  }

  const save = async () => {
    setSaving(true)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/ng-rules/${rule.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          category: draft.category,
          value: draft.value,
          action: draft.action,
          escalation_target: draft.escalation_target,
          enabled: draft.enabled,
          notes: draft.notes,
        }),
      })
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        throw new Error(d.detail || r.status)
      }
      setEditing(false)
      onChanged()
    } catch (e) {
      alert(`更新失敗: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!window.confirm(`「${rule.value}」を削除します。よろしいですか？`)) return
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/ng-rules/${rule.id}`, {
      method: 'DELETE', headers: authHeaders(),
    })
    if (r.ok) {
      onChanged()
    } else {
      alert('削除失敗')
    }
  }

  const toggleEnabled = async () => {
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/ng-rules/${rule.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ enabled: !rule.enabled }),
    })
    if (r.ok) onChanged()
  }

  const ab = ACTION_BADGE[rule.action] || ACTION_BADGE.block

  if (editing) {
    return (
      <div className={`${CARD} p-2.5`} style={{ borderLeft: '4px solid var(--color-fill-emphasis)' }}>
        <div className='grid grid-cols-[120px_1fr_100px_100px_auto] items-center gap-1.5'>
          <input value={draft.category || ''} onChange={e => setDraft({ ...draft, category: e.target.value })} placeholder='カテゴリ' className={`${INPUT} w-full`} />
          <input value={draft.value || ''} onChange={e => setDraft({ ...draft, value: e.target.value })} placeholder='値' className={`${INPUT} w-full`} />
          <select value={draft.action || 'block'} onChange={e => setDraft({ ...draft, action: e.target.value })} className={`${INPUT} w-full`}>
            <option value='block'>ブロック</option>
            <option value='log_only'>ログのみ</option>
            <option value='escalate'>エスカレ</option>
          </select>
          {ruleType === 'force_escalation' || draft.action === 'escalate' ? (
            <select value={draft.escalation_target || ''} onChange={e => setDraft({ ...draft, escalation_target: e.target.value })} className={`${INPUT} w-full`}>
              <option value=''>-</option>
              <option value='human'>人間</option>
              <option value='ai_staff'>AIスタッフ</option>
            </select>
          ) : <span />}
          <div className='flex gap-1'>
            <Button color={ButtonColor.PRIMARY} onClick={save} disabled={saving}>✓</Button>
            <Button color={ButtonColor.SECONDARY} onClick={() => { setDraft({ ...rule }); setEditing(false) }}>×</Button>
          </div>
        </div>
        <input
          value={draft.notes || ''}
          onChange={e => setDraft({ ...draft, notes: e.target.value })}
          placeholder='メモ（任意）'
          className={`${INPUT} mt-1 w-full`}
        />
      </div>
    )
  }

  return (
    <div className={`rounded-lg border border-secondary bg-surface-secondary p-2 ${rule.enabled ? '' : 'opacity-50'}`}>
      <div className='flex flex-wrap items-center gap-2'>
        <span className='text-tertiary font-mono text-[9px]'>#{rule.id}</span>
        <span className={`${ab.cls} rounded px-1.5 py-px text-[10px] font-bold`}>
          {ab.label}
        </span>
        <span className='text-primary flex-1 text-[13px] font-medium break-words'>
          {rule.value}
        </span>
        {rule.action === 'escalate' && rule.escalation_target && (
          <span className='bg-surface-emphasis text-emphasis rounded px-1.5 py-px text-[9px] font-bold'>
            → {rule.escalation_target === 'human' ? '人間' : 'AIスタッフ'}
          </span>
        )}
        <button
          onClick={toggleEnabled}
          title={rule.enabled ? '無効化' : '有効化'}
          className={`relative h-4 w-7 shrink-0 cursor-pointer rounded-full ${rule.enabled ? 'bg-fill-brand' : 'bg-fill-tertiary'}`}
        >
          <span className='bg-surface shadow-area absolute top-0.5 h-3 w-3 rounded-full transition-all' style={{ left: rule.enabled ? 12 : 2 }} />
        </button>
        <Button color={ButtonColor.SECONDARY} onClick={() => setEditing(true)}>編集</Button>
        <Button color={ButtonColor.CRITICAL} onClick={remove}>削除</Button>
      </div>
      {rule.notes && (
        <div className='text-secondary mt-1 ml-[60px] text-[11px]'>{rule.notes}</div>
      )}
    </div>
  )
}


// =====================
// 新規追加フォーム
// =====================
function NewRuleForm({ ruleType, siteId, authHeaders, onCreated }) {
  const [form, setForm] = useState({ category: '', value: '', action: 'block', escalation_target: 'human', notes: '' })
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (!form.value.trim()) {
      alert('値を入力してください')
      return
    }
    setSaving(true)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/ng-rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          rule_type: ruleType,
          category: form.category,
          value: form.value.trim(),
          action: ruleType === 'force_escalation' ? 'escalate' : form.action,
          escalation_target: form.escalation_target,
          notes: form.notes,
          enabled: true,
        }),
      })
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        throw new Error(d.detail || r.status)
      }
      setForm({ category: '', value: '', action: 'block', escalation_target: 'human', notes: '' })
      onCreated()
    } catch (e) {
      alert(`作成失敗: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  const showEscalation = ruleType === 'force_escalation' || form.action === 'escalate'
  const valueLabel = ruleType === 'ng_word' ? 'NGワード' : (ruleType === 'banned_topic' ? '禁止トピック' : '検知トリガー')

  return (
    <div className={`${CARD} mt-3 p-3.5`}>
      <h3 className='text-primary m-0 mb-2.5 text-xs font-bold'>
        ➕ 新規追加
      </h3>
      <div className='grid grid-cols-[140px_1fr_110px_110px] items-end gap-2'>
        <Field label='カテゴリ' small>
          <input value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} placeholder='例: 価格保証' className={`${INPUT} w-full`} />
        </Field>
        <Field label={valueLabel} small required>
          <input value={form.value} onChange={e => setForm({ ...form, value: e.target.value })} placeholder={`例: ${ruleType === 'ng_word' ? '最安値' : ruleType === 'banned_topic' ? '医療助言' : 'クレーム'}`} className={`${INPUT} w-full`} />
        </Field>
        {ruleType !== 'force_escalation' && (
          <Field label='アクション' small>
            <select value={form.action} onChange={e => setForm({ ...form, action: e.target.value })} className={`${INPUT} w-full`}>
              <option value='block'>ブロック</option>
              <option value='log_only'>ログのみ</option>
              <option value='escalate'>エスカレ</option>
            </select>
          </Field>
        )}
        {showEscalation && (
          <Field label='エスカレ先' small>
            <select value={form.escalation_target} onChange={e => setForm({ ...form, escalation_target: e.target.value })} className={`${INPUT} w-full`}>
              <option value='human'>人間オペレータ</option>
              <option value='ai_staff'>AIスタッフ</option>
            </select>
          </Field>
        )}
      </div>
      <input
        value={form.notes}
        onChange={e => setForm({ ...form, notes: e.target.value })}
        placeholder='メモ（任意）'
        className={`${INPUT} mt-2 w-full`}
      />
      <div className='mt-2.5 flex justify-end'>
        <Button color={ButtonColor.PRIMARY} onClick={save} disabled={saving}>{saving ? '追加中...' : '+ 追加'}</Button>
      </div>
    </div>
  )
}


// =====================
// 検知テストオーバーレイ
// =====================
function DetectionTestOverlay({ siteId, authHeaders, onClose }) {
  const [text, setText] = useState('')
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)

  const test = async () => {
    if (!text.trim()) return
    setLoading(true)
    setResult(null)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/ng-rules/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ text }),
      })
      const d = await r.json()
      setResult(d)
    } catch (e) {
      setResult({ error: e.message })
    } finally {
      setLoading(false)
    }
  }

  return (
    <ModalOverlay onClose={onClose} className='fixed inset-0 z-modal flex items-center justify-center bg-black/60 p-5 backdrop-blur-sm'>
      <div onClick={e => e.stopPropagation()} className={`${CARD} max-h-[85vh] w-full max-w-[640px] overflow-auto p-5`}>
        <div className='mb-3 flex items-center justify-between'>
          <h3 className='text-emphasis m-0 text-sm font-bold'>🧪 NG検知テスト</h3>
          <Button color={ButtonColor.SECONDARY} onClick={onClose}>✕</Button>
        </div>
        <p className='text-secondary m-0 mb-2.5 text-xs'>
          テキストを入力して、登録されているルールに該当するか確認できます。
        </p>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          rows={4}
          placeholder='検査するテキストを入力...'
          className={TEXTAREA}
        />
        <div className='mt-2 flex justify-end'>
          <Button color={ButtonColor.PRIMARY} onClick={test} disabled={loading || !text.trim()}>
            {loading ? '検査中...' : '🔍 検査実行'}
          </Button>
        </div>

        {result && (
          <div className='mt-3.5'>
            {result.error ? (
              <div className='bg-surface-critical text-critical rounded-lg p-2.5 text-xs'>
                エラー: {result.error}
              </div>
            ) : result.matched.length === 0 ? (
              <div className='bg-surface-success text-success rounded-lg p-3.5 text-center text-xs'>
                ✓ 該当するルールはありません
              </div>
            ) : (
              <div>
                <div className={`mb-2.5 rounded-lg p-2.5 text-xs font-semibold ${result.should_block ? 'bg-surface-critical text-critical' : result.should_escalate ? 'bg-surface-emphasis text-emphasis' : 'bg-surface-warning text-warning'}`}>
                  {result.should_block && '🚫 ブロック判定'}
                  {result.should_escalate && `📞 エスカレーション判定 (→ ${result.escalation_target === 'human' ? '人間' : 'AIスタッフ'})`}
                  {!result.should_block && !result.should_escalate && '📝 ログ記録のみ'}
                  <span className='ml-2 font-normal opacity-70'>{result.matched.length} 件のルールが一致</span>
                </div>
                <div className='flex flex-col gap-1.5'>
                  {result.matched.map((m, i) => (
                    <div key={i} className='rounded border border-secondary bg-surface-secondary p-2 text-[11px]'>
                      <span className='text-tertiary'>#{m.rule_id}</span>
                      <span className='bg-surface-emphasis text-emphasis ml-1.5 rounded px-1.5 py-px text-[9px]'>{m.rule_type}</span>
                      <span className='bg-surface-secondary text-secondary ml-1.5 rounded px-1.5 py-px text-[9px]'>{m.category || '(未分類)'}</span>
                      <span className='text-primary ml-2 font-semibold'>{m.value}</span>
                      <span className='text-secondary ml-2'>action: {m.action}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </ModalOverlay>
  )
}


// =====================
// 検知ログビュー（専用テーブル / 画面09 タブ5）
// =====================
function DetectionLogsView({ siteId, authHeaders }) {
  const [logs, setLogs] = useState([])
  const [counts, setCounts] = useState({ counts_by_type: {}, counts_by_severity: {} })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [days, setDays] = useState(7)
  const [filterType, setFilterType] = useState('')
  const [filterSeverity, setFilterSeverity] = useState('')
  const [filterRole, setFilterRole] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const fetchLogs = async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ days: String(days), page: String(page), per_page: '100' })
      if (filterType) params.set('rule_type', filterType)
      if (filterSeverity) params.set('severity', filterSeverity)
      if (filterRole) params.set('role', filterRole)
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/ng-rules/detection-logs?${params}`, { headers: authHeaders() })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      setLogs(d.items || [])
      setCounts({ counts_by_type: d.counts_by_type || {}, counts_by_severity: d.counts_by_severity || {} })
      setTotalPages(d.total_pages || 1)
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { fetchLogs() }, [siteId, days, filterType, filterSeverity, filterRole, page])

  const SEVERITY_BADGE = {
    high: { cls: 'bg-surface-critical text-critical', label: '🔴 高' },
    medium: { cls: 'bg-surface-warning text-warning', label: '🟡 中' },
    low: { cls: 'bg-surface-secondary text-tertiary', label: '⚪ 低' },
  }

  return (
    <div>
      <div className={`${CARD} mb-3 p-3`}>
        <div className='flex flex-wrap items-center gap-3.5'>
          <div>
            <span className='text-secondary text-[10px]'>🔴 高</span>
            <div className='text-critical text-lg font-bold'>{counts.counts_by_severity.high || 0}</div>
          </div>
          <div>
            <span className='text-secondary text-[10px]'>🟡 中</span>
            <div className='text-warning text-lg font-bold'>{counts.counts_by_severity.medium || 0}</div>
          </div>
          <div>
            <span className='text-secondary text-[10px]'>⚪ 低</span>
            <div className='text-tertiary text-lg font-bold'>{counts.counts_by_severity.low || 0}</div>
          </div>
          <div className='h-[30px] w-px bg-fill-tertiary' />
          {Object.entries(counts.counts_by_type).map(([k, v]) => (
            <div key={k}>
              <span className='text-secondary text-[10px]'>{k === 'ng_word' ? 'NGワード' : k === 'banned_topic' ? '禁止トピック' : 'エスカレ'}</span>
              <div className='text-primary text-base font-bold'>{v}</div>
            </div>
          ))}
        </div>
      </div>

      {/* フィルター */}
      <div className='mb-3 flex flex-wrap gap-1.5'>
        <select value={days} onChange={e => { setDays(parseInt(e.target.value)); setPage(1) }} className={INPUT}>
          <option value={1}>過去24h</option>
          <option value={7}>過去7日</option>
          <option value={30}>過去30日</option>
          <option value={90}>過去90日</option>
        </select>
        <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1) }} className={INPUT}>
          <option value=''>種別すべて</option>
          <option value='ng_word'>NGワード</option>
          <option value='banned_topic'>禁止トピック</option>
          <option value='force_escalation'>エスカレーション</option>
        </select>
        <select value={filterSeverity} onChange={e => { setFilterSeverity(e.target.value); setPage(1) }} className={INPUT}>
          <option value=''>重大度すべて</option>
          <option value='high'>🔴 高</option>
          <option value='medium'>🟡 中</option>
          <option value='low'>⚪ 低</option>
        </select>
        <select value={filterRole} onChange={e => { setFilterRole(e.target.value); setPage(1) }} className={INPUT}>
          <option value=''>ロールすべて</option>
          <option value='user'>ユーザー入力</option>
          <option value='assistant'>AI出力</option>
        </select>
        <Button color={ButtonColor.PRIMARY} onClick={fetchLogs}>🔄 更新</Button>
      </div>

      {error && <div className='bg-surface-critical text-critical mb-2.5 rounded-lg p-2.5 text-xs'>{error}</div>}

      {loading ? (
        <div className='text-secondary py-8 text-center text-xs'>読み込み中...</div>
      ) : logs.length === 0 ? (
        <div className={`${CARD} text-tertiary py-8 text-center text-xs`}>
          検知ログがありません
        </div>
      ) : (
        <>
          <div className={`${CARD} overflow-x-auto`}>
            <table className='w-full border-collapse text-xs'>
              <thead>
                <tr className='bg-surface-secondary'>
                  <th className={TH}>日時</th>
                  <th className={TH}>重大度</th>
                  <th className={TH}>種別</th>
                  <th className={TH}>カテゴリ</th>
                  <th className={TH}>該当値</th>
                  <th className={TH}>アクション</th>
                  <th className={TH}>ロール</th>
                  <th className={TH}>会話</th>
                  <th className={TH}>周辺テキスト</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(l => {
                  const sb = SEVERITY_BADGE[l.severity] || SEVERITY_BADGE.low
                  return (
                    <tr key={l.id} className='border-b border-secondary'>
                      <td className={`${TD} text-tertiary text-[10px]`}>
                        {l.detected_at ? new Date(l.detected_at).toLocaleString('ja-JP') : '-'}
                      </td>
                      <td className={TD}>
                        <span className={`${sb.cls} rounded px-1.5 py-px text-[10px] font-bold`}>
                          {sb.label}
                        </span>
                      </td>
                      <td className={`${TD} text-[11px]`}>{l.rule_type}</td>
                      <td className={`${TD} text-secondary text-[11px]`}>{l.category || '-'}</td>
                      <td className={`${TD} font-semibold`}>{l.matched_value}</td>
                      <td className={`${TD} text-[11px]`}>
                        <span className={`rounded px-1.5 py-px text-[10px] font-bold ${l.action_taken === 'block' ? 'bg-surface-critical text-critical' : l.action_taken === 'escalate' ? 'bg-surface-emphasis text-emphasis' : 'bg-surface-warning text-warning'}`}>{l.action_taken}</span>
                      </td>
                      <td className={`${TD} text-[11px]`}>{l.role === 'user' ? '👤' : '🤖'}</td>
                      <td className={`${TD} text-tertiary font-mono text-[11px]`}>
                        {l.conversation_id ? `#${l.conversation_id}` : '-'}
                      </td>
                      <td className={`${TD} text-secondary max-w-[280px] overflow-hidden text-[10px] text-ellipsis whitespace-nowrap`} title={l.text_snippet}>
                        {l.text_snippet || '-'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className='mt-3 flex items-center justify-center gap-1.5 text-xs'>
              <Button color={ButtonColor.SECONDARY} onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}>前へ</Button>
              <span className='text-secondary'>{page} / {totalPages}</span>
              <Button color={ButtonColor.SECONDARY} onClick={() => setPage(page + 1)} disabled={page >= totalPages}>次へ</Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}


// =====================
// 共通スタイル
// =====================
function Field({ label, hint, children, small, required }) {
  return (
    <div className={FIELD}>
      <label className={LABEL}>
        {label}{required && <span className='text-critical ml-0.5'>*</span>}
        {hint && <span className='text-tertiary ml-1.5 text-[10px] font-normal'>— {hint}</span>}
      </label>
      {children}
    </div>
  )
}
